import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { api, formatApiError } from "@/lib/api";
import { formatINR } from "@/lib/constants";
import { toast } from "sonner";
import {
  Copy, ExternalLink, MessageCircle, RefreshCw, CheckCircle2, Clock, Smartphone,
} from "lucide-react";

export default function PendingCollections() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/payouts/pending-collections");
      setItems(data.items || []);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const copyLink = (url) => {
    if (!url) return;
    navigator.clipboard?.writeText(url);
    toast.success("Link copied");
  };

  const shareWhatsApp = (txn) => {
    const amount = txn.collected_amount;
    const msg = encodeURIComponent(
      `Hi! Please pay ₹${amount} for your transfer via this secure link: ${txn.pg_payment_link_url}`
    );
    window.open(`https://wa.me/91${txn.customer_mobile}?text=${msg}`, "_blank");
  };

  const shareSMS = (txn) => {
    const msg = `Pay ₹${txn.collected_amount}: ${txn.pg_payment_link_url}`;
    window.location.href = `sms:+91${txn.customer_mobile}?body=${encodeURIComponent(msg)}`;
  };

  const checkStatus = async (txn) => {
    setChecking(txn.id);
    try {
      const { data } = await api.post(`/payouts/${txn.id}/check-status`, {});
      if (data.status === "collected" || data.status === "already_collected") {
        toast.success("Payment received! Routing to L1 approvals.");
        load();
      } else {
        toast.info(`Still ${data.status}. ${data.reason || ""}`);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Check failed");
    } finally {
      setChecking(null);
    }
  };

  const humanAge = (iso) => {
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return `${Math.floor(diff)}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  return (
    <DashboardLayout>
      <div className="animate-fade-up">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <span className="overline">Pending collections</span>
            <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-2">
              Awaiting customer payment
            </h1>
            <p className="text-muted-foreground mt-2 max-w-2xl">
              Links and in-shop sessions where the customer hasn't paid yet. Re-share, or click
              Check status to poll the gateway manually (useful when webhooks lag).
            </p>
          </div>
          <Button variant="outline" onClick={load} disabled={loading} data-testid="reload-collections">
            <RefreshCw className="h-4 w-4 mr-1" strokeWidth={1.5} /> Reload
          </Button>
        </div>

        <div className="mt-8 surface-card overflow-hidden">
          <div className="p-4 flex items-center justify-between">
            <Badge variant="outline" className="font-mono text-[10px]" data-testid="pending-count">
              {items.length} pending
            </Badge>
            <span className="text-xs text-muted-foreground">Auto-refresh every minute via webhook; manually check via button below.</span>
          </div>
          <Table data-testid="pending-table">
            <TableHeader>
              <TableRow>
                <TableHead>Created</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Beneficiary</TableHead>
                <TableHead className="text-right">Collect ₹</TableHead>
                <TableHead>Mode</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={6} className="text-center py-10 text-muted-foreground">Loading…</TableCell></TableRow>
              ) : items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-12 text-muted-foreground">
                    <CheckCircle2 className="h-8 w-8 mx-auto mb-2 text-success" strokeWidth={1.25} />
                    No pending collections. Every link has been paid or initiated in-shop.
                  </TableCell>
                </TableRow>
              ) : (
                items.map((t) => (
                  <TableRow key={t.id} data-testid={`pending-row-${t.id}`}>
                    <TableCell className="text-xs text-muted-foreground font-mono">
                      <Clock className="h-3 w-3 inline mr-1" />{humanAge(t.created_at)}
                    </TableCell>
                    <TableCell className="font-mono text-xs">+91 {t.customer_mobile}</TableCell>
                    <TableCell>
                      <div className="text-sm">{t.beneficiary_snapshot?.nickname}</div>
                      <div className="text-[10px] text-muted-foreground font-mono">{t.method?.toUpperCase()}</div>
                    </TableCell>
                    <TableCell className="text-right font-mono tabular-nums">
                      {formatINR(t.collected_amount || 0)}
                      <div className="text-[10px] text-muted-foreground">{t.gateway}</div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-[10px]">
                        {t.payment_method === "link" ? "Link" : "In-shop"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="inline-flex gap-1 flex-wrap justify-end">
                        {t.payment_method === "link" && t.pg_payment_link_url && (
                          <>
                            <Button size="sm" variant="outline" onClick={() => copyLink(t.pg_payment_link_url)} data-testid={`copy-${t.id}`}>
                              <Copy className="h-3.5 w-3.5" />
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => shareWhatsApp(t)} data-testid={`wa-${t.id}`} title="Share on WhatsApp">
                              <MessageCircle className="h-3.5 w-3.5" />
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => shareSMS(t)} data-testid={`sms-${t.id}`} title="Send via SMS">
                              <Smartphone className="h-3.5 w-3.5" />
                            </Button>
                            <a href={t.pg_payment_link_url} target="_blank" rel="noopener noreferrer">
                              <Button size="sm" variant="outline" data-testid={`open-${t.id}`}>
                                <ExternalLink className="h-3.5 w-3.5" />
                              </Button>
                            </a>
                          </>
                        )}
                        <Button
                          size="sm"
                          onClick={() => checkStatus(t)}
                          disabled={checking === t.id}
                          data-testid={`check-${t.id}`}
                        >
                          {checking === t.id ? <RefreshCw className="h-3.5 w-3.5 mr-1 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1" />}
                          Check
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </DashboardLayout>
  );
}
